/**
 * Represents a collection of facets for search filtering
 * @typedef {Object} Facets
 * @description A record of filter categories and their corresponding values with count
 */
type Facets = Record<string, Record<string, number>>

/**
 * Composable for managing search filters and filter-related interactions
 *
 * @returns {Object} Search filter management methods and reactive state
 * @property {Ref<{[key: string]: string[]}>} selectedFilters - Currently selected filters
 * @property {Ref<boolean>} isFiltersBoxOpened - Indicates if the filters box is open
 * @property {Function} toggleFiltersBox - Toggle the visibility of the filters box
 * @property {Function} closeFiltersBox - Close the filters box
 * @property {ComputedRef<number>} activeFiltersCounter - Number of active filters
 * @property {ComputedRef<string[]>} getActiveFilters - List of active filter values
 * @property {Function} removeFilter - Remove a specific filter
 * @property {Function} removeAllFilters - Clear all filters
 * @property {Function} formatFilterLabel - Format filter labels for display
 * @property {Ref<Facets>} filters - Available filter facets
 * @property {Function} mergeFacets - Merge facets from multiple search results
 */
export const useSearchFilters = () => {
  /**
   * Reactive state for selected filters across different categories
   * @type {Ref<{[key: string]: string[]}>}
   */
  const selectedFilters = useState<{ [key: string]: string[] }>(
    'selected-filters',
    () => ({
      Color: [],
      Gender: [],
      macroCat: [],
      microCat: [],
      Age: [],
      size: [],
    })
  )

  /**
   * Reference to available filter facets
   * @type {Ref<Facets>}
   */
  const filters = ref<Facets>()

  /**
   * Predefined order of filter categories
   * @type {string[]}
   */
  const filtersOrder = [
    'Gender',
    'Age',
    'macroCat',
    'microCat',
    'Color',
    'size',
  ]
  const isFiltersBoxOpened = useState('isFiltersBoxOpened', () => false)

  /**
   * Toggle the visibility of the filters box
   */
  const toggleFiltersBox = () => {
    isFiltersBoxOpened.value = !isFiltersBoxOpened.value
  }

  /**
   * Close the filters box
   */
  const closeFiltersBox = () => {
    isFiltersBoxOpened.value = false
  }

  /**
   * Count the number of active filters across all categories
   * @returns {number} Total number of active filters
   */
  const activeFiltersCounter = computed(() => {
    return Object.values(selectedFilters.value).reduce((count, value) => {
      if (Array.isArray(value)) {
        return count + value.length
      }
      return count
    }, 0)
  })

  /**
   * Format filter labels for consistent display
   * @param {string} label - Original filter label
   * @returns {string} Formatted label
   */
  const formatFilterLabel = (label: string) => {
    if (label.includes(' > ')) {
      return capitalizeFirstLetter(label.split(' > ')[1])
    }
    return capitalizeFirstLetter(label)
  }

  /**
   * Get a list of all active filter values
   * @returns {string[]} Array of active filter values
   */
  const getActiveFilters = computed<string[]>(() => {
    let activeFilters: string[] = []
    for (const key in selectedFilters.value) {
      if (selectedFilters.value[key].length > 0) {
        activeFilters = activeFilters.concat(selectedFilters.value[key])
      }
    }
    return activeFilters
  })

  /**
   * Remove a specific filter value
   * @param {string} valueToRemove - Filter value to remove
   * @returns {Object} Updated selected filters
   */
  const removeFilter = (valueToRemove: string) => {
    for (const key in selectedFilters.value) {
      if (selectedFilters.value[key].includes(valueToRemove)) {
        selectedFilters.value[key] = selectedFilters.value[key].filter(
          value => value !== valueToRemove
        )
      }
    }
    return selectedFilters.value
  }

  /**
   * Remove all filters across all categories
   * @returns {Object} Empty selected filters
   */
  const removeAllFilters = () => {
    for (const key in selectedFilters.value) {
      selectedFilters.value[key] = []
    }
    return selectedFilters.value
  }

  /**
   * Merge facets from multiple search results
   * @param {Array<{facets: Facets}>} data - Array of search result facets
   * @returns {Facets} Merged and reordered facets
   */
  const mergeFacets = (data: { facets: Facets }[]): Facets => {
    const result: Facets = {}

    data.forEach(item => {
      if (!item.facets) {
        return
      }

      for (const [key, value] of Object.entries(item.facets)) {
        if (!result[key]) {
          result[key] = {}
        }

        for (const [subKey, subValue] of Object.entries(value)) {
          if (result[key][subKey]) {
            result[key][subKey] += subValue
          } else {
            result[key][subKey] = subValue
          }
        }
      }
    })

    return reorderKeys(result, filtersOrder)
  }

  return {
    selectedFilters,
    isFiltersBoxOpened,
    toggleFiltersBox,
    closeFiltersBox,
    activeFiltersCounter,
    getActiveFilters,
    removeFilter,
    removeAllFilters,
    formatFilterLabel,
    filters,
    mergeFacets,
  }
}
